import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaSearch, FaFilter, FaTh, FaList, FaStar, FaHeart, FaArrowLeft, FaCheckCircle, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { api } from '../services/api';
import { useLocale } from '../context/LocaleContext';
import { richTextToPlainText } from '../utils/richText';
import './Products.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const toPriceNumber = (value) => typeof value === 'number' ? value : parseInt(String(value || '').replace(/,/g, ''), 10) || 0;
const getEffectivePrice = (product) => {
  if (product.priceAvailable === false) return Number.POSITIVE_INFINITY;
  const price = toPriceNumber(product.price);
  const discountPrice = toPriceNumber(product.discountPrice);
  return discountPrice > 0 && discountPrice < price ? discountPrice : price;
};

const normalizeProduct = (p) => {
  const id = p._id || p.id;
  const price = typeof p.price === 'number' ? p.price : p.price;
  const rawCardImageUrl = p.cardImageUrl || p.imageUrl || '';
  const cardImageUrl = rawCardImageUrl.startsWith('/') ? `${API_URL}${rawCardImageUrl}` : rawCardImageUrl;
  return { ...p, id, price, cardImageUrl, descriptionText: richTextToPlainText(p.description) };
};

const Products = () => {
  const { t } = useTranslation();
  const { locale, localePath, formatNumber } = useLocale();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState('default');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    setSearchQuery(searchParams.get('search') || '');
  }, [searchParams]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get(`/api/products?locale=${encodeURIComponent(locale.code)}`);
        if (res.success && Array.isArray(res.data)) {
          setProducts(res.data.map(normalizeProduct));
        }
      } catch (err) {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [locale.code]);

  useEffect(() => {
    const savedFavorites = localStorage.getItem('productFavorites');
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (_) {}
    }
  }, []);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = ['all', ...new Set(products.map(p => p.category || t('products.uncategorized')))];
    return cats;
  }, [products, t]);

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...products];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.descriptionText?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.features?.some(f => f.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Sort
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => {
          const aPrice = getEffectivePrice(a);
          const bPrice = getEffectivePrice(b);
          if (!Number.isFinite(aPrice)) return 1;
          if (!Number.isFinite(bPrice)) return -1;
          return aPrice - bPrice;
        });
        break;
      case 'price-high':
        filtered.sort((a, b) => {
          const aPrice = getEffectivePrice(a);
          const bPrice = getEffectivePrice(b);
          if (!Number.isFinite(aPrice)) return 1;
          if (!Number.isFinite(bPrice)) return -1;
          return bPrice - aPrice;
        });
        break;
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name, locale.intlLocale || locale.code));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      default:
        break;
    }

    return filtered;
  }, [locale.code, locale.intlLocale, products, searchQuery, selectedCategory, sortBy]);

  // Toggle favorite
  const toggleFavorite = (productId) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId];
      localStorage.setItem('productFavorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };


  return (
    <div className="products-page">
      <section className="products-hero">
        <div className="products-shell products-hero-inner">
          <div className="products-hero-copy">
            <span className="products-eyebrow">{t('products.eyebrow')}</span>
          <h1 className="products-title">
              {t('products.title')}
          </h1>
            <p>{t('products.subtitle')}</p>
            <div className="products-hero-points">
              <span><FaCheckCircle /> {t('products.instant')}</span>
              <span><FaSyncAlt /> {t('products.updates')}</span>
              <span><FaCheckCircle /> {t('products.support')}</span>
            </div>
          </div>
          <div className="products-hero-stat" aria-label={t('products.availableCount')}>
            <strong>{formatNumber(products.length)}</strong>
            <span>{t('products.ready')}</span>
            <small>{t('products.curated')}</small>
          </div>
        </div>
      </section>

      <main className="products-shell products-main">
        {/* Search and Filter Bar */}
        <div className="products-controls" aria-label={t('products.controlsLabel')}>
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder={t('products.search')}
              aria-label={t('products.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                type="button"
                className="search-clear"
                onClick={() => setSearchQuery('')}
                aria-label={t('products.clearSearch')}
              >
                <FaTimes />
              </button>
            )}
          </div>

          <div className="controls-right">
            <button
              type="button"
              className={`filter-btn ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
              aria-expanded={showFilters}
              aria-controls="products-category-filters"
            >
              <FaFilter /> {t('products.category')}
            </button>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
              aria-label={t('products.sortLabel')}
            >
              <option value="default">{t('products.sortDefault')}</option>
              <option value="price-low">{t('products.sortPriceLow')}</option>
              <option value="price-high">{t('products.sortPriceHigh')}</option>
              <option value="name">{t('products.sortName')}</option>
              <option value="rating">{t('products.sortRating')}</option>
            </select>

            <div className="view-mode-toggle" aria-label={t('products.viewMode')}>
              <button
                type="button"
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
                title={t('products.gridView')}
                aria-label={t('products.gridView')}
                aria-pressed={viewMode === 'grid'}
              >
                <FaTh />
              </button>
              <button
                type="button"
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
                title={t('products.listView')}
                aria-label={t('products.listView')}
                aria-pressed={viewMode === 'list'}
              >
                <FaList />
              </button>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="filters-panel" id="products-category-filters">
            <div className="filter-group">
              <label>{t('products.category')}:</label>
              <div className="category-filters">
                {categories.map(cat => (
                  <button
                    type="button"
                    key={cat}
                    className={selectedCategory === cat ? 'active' : ''}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat === 'all' ? t('products.all') : cat}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Products Display */}
        {loading ? (
          <div className="no-results">
            <p>{t('products.loading')}</p>
          </div>
        ) : filteredAndSortedProducts.length === 0 ? (
          <div className="no-results">
            <p>{t('products.empty')}</p>
            <button onClick={() => {
              setSearchQuery('');
              setSelectedCategory('all');
            }}>
              {t('products.clear')}
            </button>
          </div>
        ) : (
          <div className={`products-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
            {filteredAndSortedProducts.map((product, index) => {
              const isFavorite = favorites.includes(product.id);
              const priceAvailable = product.priceAvailable !== false;
              const regularPrice = toPriceNumber(product.price);
              const effectivePrice = getEffectivePrice(product);
              const hasDiscount = effectivePrice < regularPrice;
              const discountPercent = hasDiscount ? Math.round((1 - effectivePrice / regularPrice) * 100) : 0;

              return (
                <Link
                  key={product.id}
                  to={localePath(`/products/${product.slug || product.id}`)}
                  className="product-card"
                  style={{ animationDelay: `${index * 0.1}s`, textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="product-image">
                    {product.cardImageUrl ? (
                      <img
                        src={product.cardImageUrl}
                        alt={product.name}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <span style={{ display: product.cardImageUrl ? 'none' : 'flex' }}>
                      {product.image || '📦'}
                    </span>

                    {/* Product Actions Overlay */}
                    <div className="product-actions">
                      <button
                        className={`action-btn ${isFavorite ? 'active' : ''}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(product.id); }}
                        title={t('products.addFavorite')}
                      >
                        <FaHeart />
                      </button>
                    </div>

                    {/* Stock Badge */}
                    {product.stock && product.stock < 10 && (
                      <div className={`stock-badge ${hasDiscount ? 'with-discount' : ''}`}>{t('products.limited')}</div>
                    )}
                    {hasDiscount && <div className="product-discount-badge">{t('products.discount', { count: formatNumber(discountPercent) })}</div>}
                  </div>

                  <div className="product-info">
                    <div className="product-card-heading">
                      <h3>{product.name}</h3>
                      {product.rating && (
                        <div className="product-rating">
                          <FaStar className="star-icon" />
                          <span>{product.rating}</span>
                        </div>
                      )}
                    </div>

                    {product.category && (
                      <span className="product-category">{product.category}</span>
                    )}

                    <div className="product-meta-row">
                      {product.version && <span>{t('products.version', { version: product.version })}</span>}
                      {product.lastUpdated && <span>{t('products.updated')}</span>}
                    </div>

                    <p className="product-description">{product.descriptionText}</p>

                    <div className="product-footer">
                      <div className="product-price">
                        {!priceAvailable ? (
                          <span className="price-value">{t('products.priceUnavailable')}</span>
                        ) : hasDiscount ? (
                          <div className="product-price-discounted">
                            <del>{formatNumber(regularPrice)} {product.currencyLabel || locale.currencyLabel}</del>
                            <span className="price-value">{formatNumber(effectivePrice)} {product.currencyLabel || locale.currencyLabel}</span>
                          </div>
                        ) : (
                          <span className="price-value">{formatNumber(regularPrice)} {product.currencyLabel || locale.currencyLabel}</span>
                        )}
                      </div>
                      <div className="product-actions-footer">
                        <button
                          className="buy-btn"
                          disabled={!priceAvailable || !product.paymentEnabled}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(localePath(`/checkout/${product.id}`)); }}
                        >
                          {priceAvailable && product.paymentEnabled ? t('products.buy') : t('products.paymentUnavailable')} <FaArrowLeft />
                        </button>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Results Count */}
        {filteredAndSortedProducts.length > 0 && (
          <div className="results-count">
            {t('products.results', {
              shown: formatNumber(filteredAndSortedProducts.length),
              total: formatNumber(products.length),
            })}
          </div>
        )}
      </main>

    </div>
  );
};

export default Products;
